// What the public enquiry form will not send without, and what each field has
// to look like once it holds something.
//
// Its own rules rather than the dashboard's `validation.ts`, because it is a
// different form: nine fields instead of fifteen, and no asterisks on the
// labels. What the two do share comes from `lib/fieldFormat` — a phone number
// and an email address are checked here exactly as they are on the Add enquiry
// form, so the two can never come to disagree about what a valid number is.

import { emailError, mobileError } from "../../lib/fieldFormat";
import type { PublicEnquiryAnswers } from "./types/enquiries.types";

/**
 * Everything the form insists on that it holds as a line of text — which is
 * every field it draws bar the goals, checked just below.
 *
 * Both ways of reaching somebody are required. A practitioner replying to an
 * enquiry wants the choice of either, and the pair is also what the server
 * checks for duplicates on the way in, so an enquiry arriving with only one of
 * them is one the check can only half do — see `toFieldErrors`.
 *
 * The cost is real and worth knowing: every field added to a form a stranger
 * has not decided to fill in yet is a reason to close the tab. What is here
 * is the floor of what a practitioner needs to make contact and to size a
 * plan; nothing further should join this list without something being taken
 * off it.
 */
export const REQUIRED_FIELDS = [
  "firstName",
  "lastName",
  "age",
  "gender",
  "lifestyle",
  "email",
  "phone",
  "city",
] as const;

/**
 * The one field holding a list rather than a line of text, and which still
 * cannot be left empty — the mirror of the dashboard form's own
 * `REQUIRED_LIST_FIELDS`. Kept apart because everything above is checked as a
 * string, and a goal is checked by how many were picked.
 */
export const REQUIRED_LIST_FIELDS = ["goals"] as const;

export type PublicEnquiryField = keyof PublicEnquiryAnswers;

/** The fields `fieldError` reads — every one of them but `goals`. */
export type PublicEnquiryTextField = Exclude<PublicEnquiryField, "goals">;

export type PublicEnquiryErrors = Partial<
  Record<PublicEnquiryField, string>
>;

/** Names used in "<field> is required", so a message reads like its label. */
const LABELS: Record<PublicEnquiryField, string> = {
  firstName: "First name",
  lastName: "Last name",
  age: "Age",
  gender: "This",
  goals: "Goal",
  lifestyle: "Life style",
  email: "Email address",
  phone: "Phone number",
  city: "City",
};

/** A person's name: letters, plus the joiners that appear inside one. */
const NAME_PATTERN = /^[A-Za-z][A-Za-z\s.'-]*[A-Za-z.]$|^[A-Za-z]$/;

/** The oldest age this form will take as typed rather than as a slip — the
 *  same ceiling the pre-consultation form holds its own Age box to. */
const MAX_AGE = 120;

const isRequired = (field: PublicEnquiryTextField): boolean =>
  (REQUIRED_FIELDS as readonly string[]).includes(field);

/** Empty string means the field is fine. */
export const fieldError = (
  field: PublicEnquiryTextField,
  raw: string
): string => {
  const value = raw.trim();

  if (!value) return isRequired(field) ? `${LABELS[field]} is required` : "";

  switch (field) {
    case "firstName":
    case "lastName":
      return NAME_PATTERN.test(value)
        ? ""
        : `${LABELS[field]} can only contain letters`;
    case "age": {
      const age = Number(value);
      return Number.isFinite(age) && age >= 1 && age <= MAX_AGE
        ? ""
        : "Enter an age between 1 and 120";
    }
    case "phone":
      return mobileError(LABELS.phone, value);
    case "email":
      return emailError(value);
    default:
      return "";
  }
};

/**
 * The list fields that have to hold something. An empty one reads as the same
 * "<field> is required" a blank dropdown does, so the form says the same thing
 * about a goal whether it takes one answer or several — the dashboard form's
 * `listFieldError`, word for word.
 */
export const listFieldError = (
  field: (typeof REQUIRED_LIST_FIELDS)[number],
  values: string[]
): string => (values.length === 0 ? `${LABELS[field]} is required` : "");

/** Only failing fields appear, so an empty object means the form is valid. */
export const validate = (
  answers: PublicEnquiryAnswers
): PublicEnquiryErrors => {
  const errors: PublicEnquiryErrors = {};

  (Object.keys(answers) as PublicEnquiryField[]).forEach((field) => {
    if (field === "goals") return;
    const message = fieldError(field, answers[field]);
    if (message) errors[field] = message;
  });

  REQUIRED_LIST_FIELDS.forEach((field) => {
    const message = listFieldError(field, answers[field]);
    if (message) errors[field] = message;
  });

  return errors;
};

/** What the form opens on — every field empty. */
export const EMPTY_ANSWERS: PublicEnquiryAnswers = {
  firstName: "",
  lastName: "",
  age: "",
  gender: "",
  goals: [],
  lifestyle: "",
  email: "",
  phone: "",
  city: "",
};

/** Letters and the joiners inside a name; anything else never lands. */
export const normaliseNameInput = (raw: string): string =>
  raw.replace(/[^A-Za-z\s.'-]/g, "");
